/** Per-variant content block write paths over store actions — a component never `v-model`s onto a
 *  document field. Owner and index are getters: the index shifts under every reorder above it. */
import type {
  AssignmentContentBlock,
  AssignmentContentOwner,
} from './store/interfaces'

import { useAssignmentEditorStore } from './store'

interface UseAssignmentContentBlockEditorOptions {
  owner: () => AssignmentContentOwner
  index: () => number
}

interface UseAssignmentContentBlockTextReturn {
  setValue: (value: string) => void
}

interface UseAssignmentContentBlockImageOptions extends UseAssignmentContentBlockEditorOptions {
  /** The block object itself — the one handle on it that survives a reorder or a chapter switch. */
  block: () => AssignmentContentBlock
}

interface UseAssignmentContentBlockImageReturn {
  setUrl: (value: string) => void
  setAlt: (value: string) => void
  /**
   * A `setUrl` pinned to the block shown NOW, for a value that arrives later (an upload response).
   * The live getters cannot serve that: `ChapterEditor` reuses one list across chapters and the
   * list keys blocks by identity, so by the time the response lands this component may be gone or
   * be showing another block — and a write through `owner()` / `index()` would put the image there.
   */
  bindSetUrl: () => (value: string) => void
}

export const useAssignmentContentBlockText = (
  { owner, index }: UseAssignmentContentBlockEditorOptions,
): UseAssignmentContentBlockTextReturn => {
  const store = useAssignmentEditorStore()

  const setValue = (value: string): void => {
    store.updateTextBlock(owner(), index(), { value })
  }

  return { setValue }
}

export const useAssignmentContentBlockImage = (
  { owner, index, block }: UseAssignmentContentBlockImageOptions,
): UseAssignmentContentBlockImageReturn => {
  const store = useAssignmentEditorStore()

  const setUrl = (value: string): void => {
    store.updateImageBlock(owner(), index(), { url: value })
  }

  const setAlt = (value: string): void => {
    store.updateImageBlock(owner(), index(), { alt: value })
  }

  const bindSetUrl = (): ((value: string) => void) => {
    const target = owner()
    const targetBlock = block()

    return (value: string): void => {
      store.updateImageBlockByRef(target, targetBlock, { url: value })
    }
  }

  return { setUrl, setAlt, bindSetUrl }
}
